import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  DYNAMIC_UI_SETTING_DEFINITIONS,
  UI_SETTING_DEFINITIONS,
  type UiSettingChangeSource,
  isUiSettingSyncable,
} from '@/services/uiSettingsRegistry'

const SETTINGS_PREFIX = 'routecamp-'
const SETTINGS_METADATA_STORAGE_KEY = `${SETTINGS_PREFIX}settings.metadata`

type SettingsMetadataEntry = {
  updatedAt: number
  dirty?: boolean
}

type SettingsMetadata = {
  entries: Record<string, SettingsMetadataEntry>
}

export type UiSettingChange = {
  key: string
  value: unknown
  source: UiSettingChangeSource
}

export type SyncableUiSetting = {
  key: string
  value: unknown
  updatedAt: number
}

type UiSettingChangeListener = (change: UiSettingChange) => void

class LocalStorageSettingsService {
  private listeners = new Set<UiSettingChangeListener>()

  constructor() {
    this.migrateRenamedSettings()
  }

  private storageKey(key: string): string {
    return `${SETTINGS_PREFIX}${key}`
  }

  private hasStorage(): boolean {
    return typeof localStorage !== 'undefined'
  }

  private readMetadata(): SettingsMetadata {
    if (!this.hasStorage()) return { entries: {} }

    try {
      const raw = localStorage.getItem(SETTINGS_METADATA_STORAGE_KEY)
      if (!raw) return { entries: {} }
      const parsed = JSON.parse(raw) as Partial<SettingsMetadata>
      return {
        entries: parsed.entries && typeof parsed.entries === 'object' ? parsed.entries : {},
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to read settings metadata', { error })
      return { entries: {} }
    }
  }

  private writeMetadata(metadata: SettingsMetadata): void {
    if (!this.hasStorage()) return

    try {
      localStorage.setItem(SETTINGS_METADATA_STORAGE_KEY, JSON.stringify(metadata))
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to write settings metadata', { error })
    }
  }

  private updateMetadata(key: string, dirty: boolean, updatedAt = Date.now()): void {
    const metadata = this.readMetadata()
    metadata.entries[key] = { updatedAt, dirty }
    this.writeMetadata(metadata)
  }

  private notify(change: UiSettingChange): void {
    this.listeners.forEach((listener) => listener(change))
  }

  private migratePrefixedSetting(key: string, legacyKey: string): void {
    if (key === legacyKey) return

    const targetStorageKey = this.storageKey(key)
    const legacyStorageKey = this.storageKey(legacyKey)
    const legacyValue = localStorage.getItem(legacyStorageKey)
    if (legacyValue === null) return

    if (localStorage.getItem(targetStorageKey) === null) {
      localStorage.setItem(targetStorageKey, legacyValue)
      this.updateMetadata(key, isUiSettingSyncable(key))
    }
    localStorage.removeItem(legacyStorageKey)
  }

  private migrateRawSetting(key: string, legacyStorageKey: string): void {
    const legacyValue = localStorage.getItem(legacyStorageKey)
    if (legacyValue === null) return

    const targetStorageKey = this.storageKey(key)
    if (localStorage.getItem(targetStorageKey) === null) {
      localStorage.setItem(targetStorageKey, legacyValue)
      this.updateMetadata(key, isUiSettingSyncable(key))
    }
    localStorage.removeItem(legacyStorageKey)
  }

  private migrateRenamedSettings(): void {
    if (!this.hasStorage()) return

    try {
      UI_SETTING_DEFINITIONS.forEach((definition) => {
        definition.legacyKeys?.forEach((legacyKey) => {
          this.migratePrefixedSetting(definition.key, legacyKey)
        })
        definition.legacyLocalStorageKeys?.forEach((legacyKey) => {
          this.migrateRawSetting(definition.key, legacyKey)
        })
      })

      const existingKeys: string[] = []
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i)
        if (key) existingKeys.push(key)
      }

      DYNAMIC_UI_SETTING_DEFINITIONS.forEach((definition) => {
        if (!definition.legacyPrefix) return
        existingKeys
          .filter((key) => key.startsWith(definition.legacyPrefix ?? ''))
          .forEach((legacyKey) => {
            const suffix = legacyKey.slice((definition.legacyPrefix ?? '').length)
            if (!suffix) return
            this.migrateRawSetting(`${definition.prefix}${suffix}`, legacyKey)
          })
      })

      this.markUntrackedSyncableSettingsDirty()
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to migrate renamed settings', { error })
    }
  }

  private markUntrackedSyncableSettingsDirty(): void {
    const metadata = this.readMetadata()
    let changed = false

    this.getAllSettingsKeys()
      .filter((key) => isUiSettingSyncable(key))
      .forEach((key) => {
        if (metadata.entries[key]) return

        metadata.entries[key] = { updatedAt: Date.now(), dirty: true }
        changed = true
      })

    if (changed) {
      this.writeMetadata(metadata)
    }
  }

  saveSetting(key: string, value: unknown): void {
    if (!this.hasStorage()) return

    try {
      const serializedValue = JSON.stringify(value)
      localStorage.setItem(this.storageKey(key), serializedValue)
      this.updateMetadata(key, isUiSettingSyncable(key))
      this.notify({ key, value, source: 'local' })
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, `Failed to save setting '${key}'`, { key, error })
      throw new Error(`Failed to save setting '${key}'`)
    }
  }

  getSetting<T>(key: string): T | null {
    if (!this.hasStorage()) return null

    try {
      const item = localStorage.getItem(this.storageKey(key))
      if (item === null) {
        return null
      }
      return JSON.parse(item) as T
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, `Failed to get setting '${key}'`, { key, error })
      return null
    }
  }

  removeSetting(key: string): void {
    if (!this.hasStorage()) return

    try {
      localStorage.removeItem(this.storageKey(key))
      this.updateMetadata(key, false)
      this.notify({ key, value: null, source: 'remove' })
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, `Failed to remove setting '${key}'`, { key, error })
    }
  }

  hasSetting(key: string): boolean {
    if (!this.hasStorage()) return false

    return localStorage.getItem(this.storageKey(key)) !== null
  }

  getAllSettingsKeys(): string[] {
    if (!this.hasStorage()) return []

    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(SETTINGS_PREFIX) && key !== SETTINGS_METADATA_STORAGE_KEY) {
        keys.push(key.substring(SETTINGS_PREFIX.length))
      }
    }
    return keys
  }

  getSyncableSettings(): SyncableUiSetting[] {
    const metadata = this.readMetadata()
    return this.getAllSettingsKeys()
      .filter((key) => isUiSettingSyncable(key))
      .map((key) => ({
        key,
        value: this.getSetting(key),
        updatedAt: metadata.entries[key]?.updatedAt ?? 0,
      }))
  }

  getDirtySyncableSettings(): SyncableUiSetting[] {
    const metadata = this.readMetadata()
    return this.getSyncableSettings().filter(
      (setting) => metadata.entries[setting.key]?.dirty === true,
    )
  }

  applyRemoteSettings(
    settings: SyncableUiSetting[],
    options: { syncedKeys?: Set<string>; syncedUpdatedAtByKey?: Map<string, number> } = {},
  ): void {
    const metadata = this.readMetadata()

    settings.forEach((setting) => {
      if (!isUiSettingSyncable(setting.key)) return

      const localEntry = metadata.entries[setting.key]
      const syncedUpdatedAt = options.syncedUpdatedAtByKey?.get(setting.key)
      if (
        syncedUpdatedAt !== undefined &&
        localEntry?.updatedAt !== undefined &&
        localEntry.updatedAt > syncedUpdatedAt
      ) {
        return
      }
      const isDirtyAndNotJustSynced =
        localEntry?.dirty === true && !options.syncedKeys?.has(setting.key)
      if (isDirtyAndNotJustSynced) return

      localStorage.setItem(this.storageKey(setting.key), JSON.stringify(setting.value))
      metadata.entries[setting.key] = { updatedAt: setting.updatedAt, dirty: false }
      this.notify({ key: setting.key, value: setting.value, source: 'remote' })
    })

    this.writeMetadata(metadata)
  }

  subscribe(listener: UiSettingChangeListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  clearAllSettings(): void {
    const keys = this.getAllSettingsKeys()
    keys.forEach((key) => this.removeSetting(key))
    localStorage.removeItem(SETTINGS_METADATA_STORAGE_KEY)
  }
}

export const settingsService = new LocalStorageSettingsService()
